import {Actions, Button, FormControl, FormField, Separator} from 'platform/components';
import {Box, HStack, Icon, VStack, Text} from 'platform/foundation';

import {useFieldArray, UseFormReturn} from 'react-hook-form';

import {DragHandle, SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EMPTY_ENUM_VALUE} from '../../constants/EMPTY_ENUM_VALUE';
import {DefinitionFormValues} from '../../types/DefinitionFormValues';

const CONTAINER_ID = 'enum-fields-creation';

interface EnumFieldsCreationProps extends RequiredTestIdProps {
  control: FormControl<DefinitionFormValues>;
  formApi: UseFormReturn<DefinitionFormValues>;
}

export function EnumFieldsCreation(props: EnumFieldsCreationProps) {
  const {fields, append, remove, replace} = useFieldArray({
    control: props.control,
    name: 'enumValues',
  });
  return (
    <>
      <Separator spacing={0} />
      <VStack spacing={2} align="flex-start">
        <SortableCardList
          containerId={CONTAINER_ID}
          items={fields}
          withHandle
          disableDropAnimation
          onReorder={(reorderedItems) => {
            // This is needed because reorderedItems doesn't contain all values, so we cannot use just replace(reorderedItems)
            const currentValues = props.formApi.getValues('enumValues') ?? [];

            const reorderedValues = reorderedItems.map((item) => {
              const originalIndex = fields.findIndex((field) => field.id === item.id);
              if (originalIndex === -1) {
                return EMPTY_ENUM_VALUE;
              }

              return currentValues.at(originalIndex) ?? EMPTY_ENUM_VALUE;
            });

            replace(reorderedValues);
          }}
          renderCard={(_, index, handleProps) => (
            <HStack align="flex-start" width="100%" spacing={2}>
              <Box paddingTop={1}>
                <DragHandle {...handleProps}>
                  <Icon value="action/drag_indicator" color="palettes.neutral.60.100" />
                </DragHandle>
              </Box>
              <Box paddingTop={1} width={5}>
                <Text color="tertiary" align="center">{`${index + 1}.`}</Text>
              </Box>
              <Box flex={1}>
                <FormField
                  type="text"
                  control={props.control}
                  name={`enumValues.${index}.value`}
                  data-testid={suffixTestId('enumValue', props)}
                  isRequired
                  placeholder={i18n.t('page.generalSettings.labels.enumValue')}
                />
              </Box>
              <Actions
                actions={[
                  {
                    type: 'iconButton',
                    icon: 'action/delete',
                    severity: 'danger',
                    onClick: () => remove(index),
                    isDisabled: fields.length === 1,
                  },
                ]}
              />
            </HStack>
          )}
        />
        <Button
          leftIcon="content/add_circle"
          title={i18n.t('general.actions.addAnother')}
          size="small"
          variant="link"
          onClick={() => append(EMPTY_ENUM_VALUE)}
        />
      </VStack>
    </>
  );
}
